/**
 * Browser API Polyfills
 *
 * This file contains polyfills for modern browser APIs that may not be available
 * in older browsers. Import this file in your root layout to ensure compatibility.
 */

/**
 * UUID generation function that works in all environments
 * Provides a fallback chain for maximum compatibility:
 * 1. Native crypto.randomUUID() if available
 * 2. crypto.getRandomValues() based UUID generation
 * 3. Math.random() as final fallback
 */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback implementation using crypto.getRandomValues or Math.random
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c: string) =>
      (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16)
    );
  }

  // Final fallback using Math.random (less secure but works everywhere)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Try to polyfill crypto.randomUUID if possible
if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
  try {
    crypto.randomUUID = generateUUID as () => `${string}-${string}-${string}-${string}-${string}`;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // Silent fail - the crypto object might be read-only (e.g., Facebook in-app browser on iOS)
    console.warn('Unable to polyfill crypto.randomUUID, using fallback function');
  }
}
